import React, { useEffect, useState, useMemo } from 'react';
import { DocumentsClientService, CompanyService } from '$/services';
import { getIsCurrentUserAdminState } from '$/store/profile/selectors';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import { downloadFile } from '$/store/export/actions';
import { toastError } from '$/store/toast/actions';
import ClientCompanyDocumentsList from '$/components/client-company-documents-list';
import { connect } from 'react-redux';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { PartialCompany } from '$/components/client-company-documents-list/models';
import { getCompanyHistoryFeatureFlagState } from '$/store/release-feature-flags/selectors';
import { useMatch } from 'react-router-dom';

interface ICompanyDocumentsHistoryDetailProps {
  featureFlags?: string[];
  profile?: Bixby.Models.Profile;
  isCompanyHistoryEnabled: boolean;
  isAdmin: boolean;
  dispatchDownloadDocument: (blob: Blob, fileName: string) => Promise<void>;
  dispatchDownloadFailure: () => Promise<void>;
}

const CompanyDocumentsHistoryDetail: React.FC<ICompanyDocumentsHistoryDetailProps> = props => {
  const { featureFlags, isCompanyHistoryEnabled } = props;
  const match = useMatch('/company-documents/:companyId/*');
  const [currentCompany, setCurrentCompany] = useState<PartialCompany | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const isAuthorizedToView = useMemo(
    () => featureFlags && featureFlags.includes(Bixby.Constants.CLIENT_COMPANY_DOCUMENTS) && isCompanyHistoryEnabled,
    [featureFlags, isCompanyHistoryEnabled]
  );

  useEffect(() => {
    const getData = async () => {
      try {
        if (!match?.params?.companyId) return;
        setIsLoading(true);
        const company = await CompanyService.getHistoricalCompany(match.params.companyId);
        const historicalCompany: PartialCompany = {
          id: company.companyId,
          name: company.companyName,
          dataSiteName: company.dataSiteName,
          dataSiteProviderName: company.dataSiteProviderName,
          dataSiteHostName: company.dataSiteHostName,
          websiteUrl: company.websiteUrl,
          isSECPublic: company.isSECPublic,
        };
        setCurrentCompany(historicalCompany);
      } catch (error: any) {
        // suppress
      } finally {
        setIsLoading(false);
      }
    };

    if (match && match.params && match.params.companyId && isAuthorizedToView) {
      getData();
    }
  }, [match, isAuthorizedToView]);

  const downloadAllDocuments = async (id: string) => {
    if (!currentCompany) return;

    const { blob, filename } = await DocumentsClientService.downloadAllAsZip(id);
    await props.dispatchDownloadDocument(blob, filename || 'documents.zip');
  };

  const getCompanyDocuments = async (id: string) => await DocumentsClientService.getDocumentsByCompanyId(id);

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return currentCompany ? (
      <ClientCompanyDocumentsList
        {...props}
        getCompanyDocuments={getCompanyDocuments}
        currentCompany={currentCompany}
        isCurrentPortfolioCompanyPublic={currentCompany?.isSECPublic}
        downloadAllDocuments={downloadAllDocuments}
      />
    ) : (
      <NoDataFound dataType="company" />
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isCompanyHistoryEnabled } = getCompanyHistoryFeatureFlagState(state);
  const { isAdmin } = getIsCurrentUserAdminState(state);

  return {
    profile: state.profileState.profile,
    featureFlags: state.loginState.featureFlags,
    isCompanyHistoryEnabled,
    isAdmin,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadDocument: (blob: Blob, fileName: string) => dispatch(downloadFile(blob, fileName)),
  dispatchDownloadFailure: () => dispatch(toastError('Failed to download document')),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDocumentsHistoryDetail);
